import React, { useEffect, useState } from 'react';
import { router, useForm, usePage } from '@inertiajs/react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Edit.css';
import axios from 'axios';
import CreditsInfo from '../../Components/CreditsInfo'; // Using alias for import

export default function Edit() {
    const { 
        video, 
        section, 
        imageUrl, 
        voice_text, 
        prompt, 
        voiceUrl, 
        reload, 
        credits: initialCredits, 
        flash, 
        errors, 
        movie_hash, 
        user_email, 
        movie_title, 
        sceneMap = {}, 
        sceneTextMap = {},
        allCharacters = [] // Receive all characters
    } = usePage().props;

    const { post, processing, setData, data, put } = useForm({
        prompt: prompt || '',
        voice: 'alloy',
        voice_text: voice_text || '',
        image: null,
        description: section.description || '',
        scene_number: section.scene_number || Object.keys(sceneMap)[0] || '',
        provider: 'gpt_image_2',
        // Pre-populate with IDs of already-linked characters
        character_ids: section.characters ? section.characters.map(c => c.id) : []
    });

    const [credits, setCredits] = useState(initialCredits);
    const [overlay, setOverlay] = useState(false);
    const [selectedSceneText, setSelectedSceneText] = useState('');

    useEffect(() => {
        if (reload) {
            router.reload();
        }
    }, [reload]);

    useEffect(() => {
        if (flash?.success) {
            toast.success(flash.success);
        }
        if (flash?.error) {
            toast.error(flash.error);
        }
        if (flash?.message) {
            toast.info(flash.message);
        }
        if (flash?.status) {
            toast.info(flash.status);
        }
    }, [flash]);

    useEffect(() => {
        if (errors) {
            Object.keys(errors).forEach((key) => {
                toast.error(errors[key]);
            });
        }
    }, [errors]);

    const fetchCredits = async () => {
        try {
            const response = await axios.get(`/credits/${movie_hash}`);
            setCredits(response.data.credits);
        } catch (error) {
            console.error('Error fetching credits:', error);
            toast.error('Error fetching credits.');
        }
    };

    const handleImageGeneration = (e) => {
        e.preventDefault();
        post(`/sections/${section.id}/create-image`, {
            ...data // Send all form data including the provider
        }, {
            onSuccess: () => {
                console.log(`${data.provider} image generation started`);
                router.visit(window.location.href); // Reload the page to fetch the updated data
                fetchCredits(); // Fetch the latest credits
            }
        });
    };

    const handleAudioGeneration = (e) => {
        e.preventDefault();
        post(`/sections/${section.id}/create-audio`, {
            voice_text: data.voice_text,
            voice: data.voice
        }, {
            onSuccess: () => {
                router.visit(window.location.href);
                fetchCredits();
            }
        });
    };

    const handleImageUpload = (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('image', data.image);
        formData.append('_method', 'POST');
        post(`/sections/${section.id}/upload-image`, formData, {
            onSuccess: () => {
                setData('image', null);
                router.visit(window.location.href); // Reload the page to fetch the updated data
            }
        });
    };

const handleCreateImagePrompt = async (e) => {
  e.preventDefault();
  setOverlay(true);
  try {
    // Kick off prompt creation (returns 202)
    await axios.post(`/sections/${section.id}/create-image-prompt`, {
      description: data.description,
      scene_number: data.scene_number
    });

    // Poll for readiness
    const start = Date.now();
    const timeoutMs = 60_000; // safety timeout
    const intervalMs = 1500;

    const poll = async () => {
      const res = await axios.get(`/sections/${section.id}/prompt`);
      if (res.data?.ready && res.data?.prompt) {
        setData('prompt', res.data.prompt);
        toast.success('Image prompt ready!');
        setOverlay(false);
        return true;
      }
      return false;
    };

    // Initial attempt (fast path)
    if (await poll()) return;

    const timer = setInterval(async () => {
      // Give up after timeout
      if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        setOverlay(false);
        toast.error('Prompt generation timed out. Try again in a moment.');
        return;
      }
      if (await poll()) clearInterval(timer);
    }, intervalMs);
  } catch (error) {
    console.error('Error creating image prompt:', error);
    toast.error('Error creating image prompt.');
    setOverlay(false);
  }
};

    const handleSaveChanges = (e) => {
        e.preventDefault();
        // Use Inertia's PUT method to send all updatable data
        put(`/sections/${section.id}`, {
            onSuccess: () => toast.success('Section saved successfully!'),
            onError: () => toast.error('Failed to save section.'),
        });
    };

    const handleSceneTextModal = (sceneNumber) => {
        const sceneText = sceneTextMap[sceneNumber] || "No text available for this scene.";
        const formattedText = sceneText.split('\n').map((line, index) => (
            <React.Fragment key={index}>
                {line}
                <br />
            </React.Fragment>
        ));
        setSelectedSceneText(formattedText);

        // Initialize and show Bootstrap modal
        const sceneTextModal = new bootstrap.Modal(document.getElementById('sceneTextModal'));
        sceneTextModal.show();
    };

    return (
        <div className="container mt-4">

            {/* Overlay spinner if processing */}
            { (processing || overlay) && (
                <div className="fullscreen-spinner-overlay">
                    <div className="spinner"></div>
                </div>
            )}

            <ToastContainer />

            {/* Credits and Back Button */}
            <CreditsInfo 
                movie_title={movie_title}  
                movie_hash={movie_hash} 
                initialCredits={credits} 
                user_email={user_email} 
            />
            <br/>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <a href={`/videos/${video.id}`} className="btn btn-secondary">Back to Video</a>
                <button onClick={handleSaveChanges} className="btn btn-success" disabled={processing}>
                    Save All Changes
                </button>
            </div>

            <h1 className="my-4">
                Edit Section {section.order_number} of {video.title}
            </h1>

            {/* Instructions Card */}
            <div className="card mb-4">
                <div className="card-header" id="instructionsHeading">
                    <h2 className="mb-0">
                        <button 
                            className="btn btn-link text-decoration-none" 
                            type="button" 
                            data-bs-toggle="collapse" 
                            data-bs-target="#instructionsContent" 
                            aria-expanded="true" 
                            aria-controls="instructionsContent"
                        >
                            How to Generate an Image
                        </button>
                    </h2>
                </div>
                <div 
                    id="instructionsContent" 
                    className="collapse show" 
                    aria-labelledby="instructionsHeading"
                >
                    <div className="card-body">
                        <ol>
                            <li><strong>Select a Scene:</strong> Pick the scene number you want to focus on.</li>
                            <li><strong>Describe Your Vision:</strong> In the <em>Description</em> box, outline your main ideas (e.g., “office setting,” “business casual,” etc.).</li>
                            <li><strong>Create Image Prompt:</strong> Click “Create Image Prompt” to let the AI expand your description into a full prompt.</li>
                            <li><strong>Generate Image:</strong> Once the prompt looks good, click “Generate Image” to see the result!</li>
                        </ol>
                        <p className="mb-0">
                            You can tweak your description or the generated prompt at any time to refine the image. 
                            If anything is unclear, feel free to ask – we want this process to be as straightforward as possible!
                        </p>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* IMAGE SECTION (Left Column) */}
                <div className="col-md-6">
                    {/* Current Image Card */}
                    <div className="card mb-4" style={{ backgroundColor: '#f0f8ff' }}>
                        <div className="card-body">
                            <h2>Current Image</h2>
                            {imageUrl ? (
                                <img 
                                    src={imageUrl} 
                                    alt="Section Image" 
                                    className="img-fluid" 
                                />
                            ) : (
                                <p>No current image.</p>
                            )}
                        </div>
                    </div>

                    {/* Generate New Image Card */}
                    <div className="card mb-4" style={{ backgroundColor: '#f0f8ff' }}>
                        <div className="card-body">
                            <h2>Generate New Image</h2>

                            {/* Scene Number */}
                            <label htmlFor="scene_number" className="form-label">
                                Scene Number
                                <span 
                                    className="ms-2 text-muted" 
                                    data-bs-toggle="tooltip" 
                                    title="Select which scene you want to generate an image for."
                                    style={{ cursor: 'pointer' }}
                                >
                                    &#9432;
                                </span>
                            </label>
                            <select
                                id="scene_number"
                                value={data.scene_number}
                                onChange={(e) => setData('scene_number', e.target.value)}
                                className="form-control mb-2"
                                required
                            >
                                {Object.entries(sceneMap).map(([number, title]) => (
                                    <option key={number} value={number}>
                                        {number} - {title}
                                    </option>
                                ))}
                            </select>
                            <button 
                                type="button" 
                                className="btn btn-link p-0 mb-3" 
                                onClick={() => handleSceneTextModal(data.scene_number)}
                            >
                                View Scene
                            </button>

                            {/* Form for creating and submitting an image */}
                            <form onSubmit={handleImageGeneration}>
                                {/* Character Multi-select */}
                                <div className="mb-3">
                                    <label htmlFor="character_ids" className="form-label">
                                        Characters in Scene
                                        <span className="ms-2 text-muted" data-bs-toggle="tooltip" title="Select characters to ensure their appearance is used as a reference.">
                                            &#9432;
                                        </span>
                                    </label>
                                    <select
                                        multiple
                                        id="character_ids"
                                        className="form-control"
                                        value={data.character_ids}
                                        onChange={e => setData('character_ids', [...e.target.selectedOptions].map(o => o.value))}
                                    >
                                        {allCharacters.map(char => (
                                            <option key={char.id} value={char.id}>{char.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Description */}
                                <div className="mb-3">
                                    <label htmlFor="description" className="form-label">
                                        Description
                                        <span 
                                            className="ms-2 text-muted" 
                                            data-bs-toggle="tooltip" 
                                            title="Explain what you're picturing. E.g. 'Sunlit office with people in business casual.'"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            &#9432;
                                        </span>
                                    </label>
                                    <textarea
                                        name="description"
                                        className="form-control"
                                        rows="4"
                                        value={data.description}
                                        onChange={(e) => setData('description', e.target.value)}
                                        placeholder="Enter description here"
                                    />
                                </div>

                                {/* Create Image Prompt Button */}
                                <button
                                    type="button"
                                    className="btn btn-primary mb-3"
                                    onClick={handleCreateImagePrompt}
                                    disabled={!data.description}
                                >
                                    Create Image Prompt
                                </button>

                                {/* Prompt */}
                                <div className="mb-3">
                                    <label htmlFor="prompt" className="form-label">
                                        Image Prompt
                                        <span 
                                            className="ms-2 text-muted" 
                                            data-bs-toggle="tooltip" 
                                            title="This is the AI-ready prompt. You can edit it directly if you want changes."
                                            style={{ cursor: 'pointer' }}
                                        >
                                            &#9432;
                                        </span>
                                    </label>
                                    <textarea
                                        name="prompt"
                                        className="form-control"
                                        rows="11"
                                        value={data.prompt}
                                        onChange={(e) => setData('prompt', e.target.value)}
                                        placeholder="The AI prompt will appear here..."
                                        required
                                    />
                                </div>

                                {/* Provider */}
                                <div className="mb-3">
                                    <label htmlFor="provider" className="form-label">
                                        Image Provider
                                        <span 
                                            className="ms-2 text-muted" 
                                            data-bs-toggle="tooltip" 
                                            title="Choose the AI service you want to use. Each has different costs."
                                            style={{ cursor: 'pointer' }}
                                        >
                                            &#9432;
                                        </span>
                                    </label>
                                    <select
                                        name="provider"
                                        className="form-control"
                                        value={data.provider}
                                        onChange={(e) => setData('provider', e.target.value)}
                                        required
                                    >
                                        <option value="gpt_image_2">GPT Image 2.5 Flare — low (2 credits, default)</option>
                                        <option value="gemini_flash">Gemini Flash (Multimodal)</option>
                                        {/* No leonardo: it can't take cast reference images (owner, 2026-10-07).
                                            No imagen / LemonFox: retired 2026-10-12 (no-training policy). */}
                                    </select>
                                </div>

                                {/* Generate Image Button */}
                                <button
                                    type="submit"
                                    className="btn btn-primary mb-3"
                                    disabled={processing || !data.prompt}
                                >
                                    Generate Image
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Upload New Image Card */}
                    <div className="card mb-4" style={{ backgroundColor: '#f0f8ff' }}>
                        <div className="card-body">
                            <h2>Upload New Image</h2>
                            <form onSubmit={handleImageUpload}>
                                <div className="mb-3">
                                    <label htmlFor="image" className="form-label">
                                        Upload Image
                                        <span 
                                            className="ms-2 text-muted" 
                                            data-bs-toggle="tooltip" 
                                            title="Upload a custom image file from your computer."
                                            style={{ cursor: 'pointer' }}
                                        >
                                            &#9432;
                                        </span>
                                    </label>
                                    <input
                                        type="file"
                                        name="image"
                                        className="form-control"
                                        onChange={(e) => setData('image', e.target.files[0])}
                                        required
                                    />
                                    {errors.image && (
                                        <div className="text-danger">{errors.image}</div>
                                    )}
                                </div>
                                <button 
                                    type="submit" 
                                    className="btn btn-secondary" 
                                    disabled={processing}
                                >
                                    Upload Image
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* AUDIO SECTION (Right Column) */}
                <div className="col-md-6">
                    {/* Current Audio Card */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <h2>Current Audio</h2>
                            <p>
                                {voiceUrl ? (
                                    <audio controls src={voiceUrl}></audio>
                                ) : (
                                    'No current audio.'
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Generate New Audio Card */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <h2>Generate New Audio</h2>
                            <form onSubmit={handleAudioGeneration}>
                                {/* Voiceover Text */}
                                <div className="mb-3">
                                    <label htmlFor="text" className="form-label">
                                        Voiceover Text
                                        <span 
                                            className="ms-2 text-muted" 
                                            data-bs-toggle="tooltip" 
                                            title="Enter the dialogue or narration text here."
                                            style={{ cursor: 'pointer' }}
                                        >
                                            &#9432;
                                        </span>
                                    </label>
                                    <textarea
                                        name="text"
                                        className="form-control"
                                        rows="2"
                                        value={data.voice_text}
                                        onChange={(e) => setData('voice_text', e.target.value)}
                                        placeholder="Enter voiceover text here"
                                        required
                                    />
                                </div>

                                {/* Voice */}
                                <div className="mb-3">
                                    <label htmlFor="voice" className="form-label">
                                        Voice
                                        <span 
                                            className="ms-2 text-muted" 
                                            data-bs-toggle="tooltip" 
                                            title="Pick the style of AI voice you’d like to use."
                                            style={{ cursor: 'pointer' }}
                                        >
                                            &#9432;
                                        </span>
                                    </label>
                                    <select
                                        name="voice"
                                        className="form-control"
                                        value={data.voice}
                                        onChange={(e) => setData('voice', e.target.value)}
                                        required
                                    >
                                        <option value="alloy">Alloy</option>
                                        <option value="echo">Echo</option>
                                        <option value="fable">Fable</option>
                                        <option value="onyx">Onyx</option>
                                        <option value="nova">Nova</option>
                                        <option value="shimmer">Shimmer</option>
                                    </select>
                                </div>

                                <button 
                                    type="submit" 
                                    className="btn btn-primary" 
                                    disabled={processing}
                                >
                                    Generate Audio (1 credit)
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scene Text Modal */}
            <div 
                className="modal fade" 
                id="sceneTextModal" 
                tabIndex="-1" 
                aria-labelledby="sceneTextModalLabel" 
                aria-hidden="true"
            >
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 
                                className="modal-title" 
                                id="sceneTextModalLabel"
                            >
                                Scene Text
                            </h5>
                            <button 
                                type="button" 
                                className="btn-close" 
                                data-bs-dismiss="modal" 
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <center>{selectedSceneText}</center>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
